import {compact} from 'lodash';
import React, {FC, useCallback, useMemo, useState} from 'react';
import styled from 'styled-components';

import {useLocalize} from '../../../../libs/shared/core/src/helpers/internationalization/internationalizationContext';
import {defineMessage, Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {ChannelModel} from '../../../../libs/shared/core/src/models/api/channelModel';
import {ContactSourcesEnum} from '../../../../libs/shared/core/src/models/api/contactModel';
import {LoadedMessageModel, SendActionsEnum} from '../../../../libs/shared/core/src/models/api/messageModel';
import {RecipientRolesEnum} from '../../../../libs/shared/core/src/models/api/recipientModel';
import {reportToBugsnag} from '../../../../libs/shared/core/src/services/bugsnag/bugsnag';
import {isTransientError} from '../../../../libs/shared/core/src/services/http/httpError';
import {CellsContext} from '../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {TextAreaBox} from '../../../../libs/shared/ui/src/components/boxedInputs/textAreaBox';
import {TextBox} from '../../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroup} from '../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroup';
import {ButtonGroupSpacer} from '../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroupSpacer';
import {Dialog} from '../../../../libs/shared/ui/src/components/dialogs/dialog';
import {DialogContent} from '../../../../libs/shared/ui/src/components/dialogs/dialogContent';
import {DialogFooter} from '../../../../libs/shared/ui/src/components/dialogs/dialogFooter';
import {DialogHeader} from '../../../../libs/shared/ui/src/components/dialogs/dialogHeader';
import {useAppDispatch, useAppSelector} from '../../../helpers/appReduxHelpers';
import {isValidEmailAddress} from '../../../helpers/emailHelpers';
import {buildMessagePatcherFactory} from '../../../helpers/models/messagePatchHelpers';
import {parseRecipient} from '../../../helpers/models/recipientHelpers';
import {createDraftMessageAction} from '../../../store/data/messages/draftMessagesActions';
import {saveMessageAsyncAction} from '../../../store/data/messages/saveMessageActions';
import {sendMessageAsyncAction} from '../../../store/data/messages/sendMessageActions';
import {getUser} from '../../../store/data/teammates/teammatesSelectors';
import {navigateToConversation} from '../../../store/stateless/navigationActions';
import {CustomerPortalChannelPickerContainer} from '../composer/channelPicker/customerPortalChannelPickerContainer';

/*
 * Props.
 */

interface CreateCustomerPortalDialogProps {
  message: LoadedMessageModel;
  cellsContext: CellsContext;
  onRequestClose: () => void;
}

/*
 * Style.
 */

const StyledButtonGroup = styled(ButtonGroup)`
  width: 100%;
`;

const StyledDescriptionP = styled.p`
  color: ${(p) => p.theme.greys.shade80};
  line-height: 22px;
  margin: 0px 0px 16px 0px;
`;

/*
 * Component.
 */

export const CreateCustomerPortalDialog: FC<CreateCustomerPortalDialogProps> = (props) => {
  const {message, cellsContext, onRequestClose} = props;
  const [email, setEmail] = useState(
    (() => {
      const from = message.recipients.find((r) => r.role === RecipientRolesEnum.FROM);
      if (from && from.handle && isValidEmailAddress(from.handle)) {
        return from.handle;
      }
      return '';
    })(),
  );
  const [channelId, setChannelId] = useState<number>();
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState(message.text);
  const [isSaving, setIsSaving] = useState(false);

  const user = useAppSelector(getUser);

  const localize = useLocalize();
  const dispatch = useAppDispatch();

  const onRequestCloseWrapped = () => {
    if (isSaving) {
      return;
    }
    onRequestClose();
  };

  const onSaveAsync = async () => {
    try {
      setIsSaving(true);
      // Construct the draft message from the agent to the customer.
      const draftMessage = dispatch(
        createDraftMessageAction({
          originalTransitionConversationId: message.conversationId,
          channelId,
          subject,
          text: description,
          shouldIncludePortalChannels: true,
          recipients: compact([
            parseRecipient(ContactSourcesEnum.EMAIL, RecipientRolesEnum.TO, email),
            parseRecipient(ContactSourcesEnum.EMAIL, RecipientRolesEnum.FROM, user.email),
          ]),
        }),
      );

      // Save the draft message.
      const saveResponse = await dispatch(
        saveMessageAsyncAction(draftMessage.uid, {
          patchMessage: buildMessagePatcherFactory()({}),
          getVersion: () => draftMessage.version,
        }),
      );

      await dispatch(
        sendMessageAsyncAction(
          draftMessage.uid,
          {
            sendAction: SendActionsEnum.SEND,
            cellsContext,
          },
          {
            getVersion: () => draftMessage.version,
          },
        ),
      );

      // Navigate to the conversation.
      if (saveResponse.savedMessage?.conversationId) {
        dispatch(navigateToConversation(saveResponse.savedMessage.conversationId));
      }

      onRequestClose();
    } catch (error) {
      if (!isTransientError(error)) {
        reportToBugsnag(error);
      }
      // TODO: Show error message
    } finally {
      setIsSaving(false);
    }
  };

  const isValid = useMemo(() => {
    if (!email.trim() || !isValidEmailAddress(email)) {
      return false;
    }
    if (!channelId) {
      return false;
    }
    if (!subject.trim()) {
      return false;
    }
    if (!description.trim()) {
      return false;
    }
    return true;
  }, [channelId, description, email, subject]);

  const onChannelSelected = useCallback((channel: ChannelModel) => setChannelId(channel.id), []);

  return (
    <Dialog onRequestClose={onRequestCloseWrapped}>
      <DialogHeader
        title={<Trans id="customer-portal-dialog-header">Create customer portal conversation</Trans>}
      />
      <DialogContent>
        <StyledDescriptionP>
          <Trans id="customer-portal-dialog-description-text">
            Creating a customer portal conversation will allow the customer to receive updates and track the
            status of their request.
          </Trans>
        </StyledDescriptionP>
        <CustomerPortalChannelPickerContainer channelId={channelId} onChannelSelected={onChannelSelected} />
        <TextBox
          label={<Trans id="customer-portal-dialog-email">Customer email address</Trans>}
          value={email}
          onChange={setEmail}
          isRequired
        />
        <TextBox
          label={<Trans id="customre-portal-dialog-subject">Subject</Trans>}
          placeholder={localize(
            defineMessage({
              id: 'customer-portal-dialog-subject-placeholder',
              message: 'Subject',
            }),
          )}
          value={subject}
          onChange={setSubject}
          isRequired
        />
        <TextAreaBox
          label={<Trans id="customer-portal-dialog-description">Details</Trans>}
          value={description}
          onChange={setDescription}
          shouldAllowResize
          isRequired
          rows={3}
        />
      </DialogContent>
      <DialogFooter>
        <StyledButtonGroup>
          <ButtonGroupSpacer />
          <Button onClick={onRequestCloseWrapped} isDisabled={isSaving}>
            <Trans id="customer-portal-dialog-cancel">Cancel</Trans>
          </Button>
          <Button
            variant={ButtonVariantsEnum.PRIMARY}
            isDisabled={!isValid || isSaving}
            onClick={onSaveAsync}
          >
            <Trans id="customer-portal-dialog-create">Create portal conversation</Trans>
          </Button>
        </StyledButtonGroup>
      </DialogFooter>
    </Dialog>
  );
};
